import React from "react";
import {
    Server,
    MapPin,
    User,
    Tag,
    Clock,
    Printer,
    ShieldAlert,
    FileText,
    Hash,
    Fingerprint
} from "lucide-react";

interface SnmpData {
    id: string;
    agent_id: string;
    asset_id: string;
    client_id: string;
    default_community_string: string;
    default_snmp_string: boolean;
    description: string;
    deviceLocation: string;
    isPrinter: boolean;
    oids: Record<string, any>;
    organization_id: string;
    serial: string;
    sysContact: string;
    sysName: string;
    sysObjectId: string;
    updated: string;
}

interface SnmpInfoProps {
    data: SnmpData;
}

const SnmpInfo: React.FC<SnmpInfoProps> = ({ data }) => {
    if (!data) return null;

    const {
        sysName,
        description,
        deviceLocation,
        sysContact,
        sysObjectId,
        serial,
        updated,
        isPrinter,
        default_snmp_string,
        default_community_string,
    } = data;

    // Format date
    const lastUpdated = updated
        ? new Date(updated).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
        : "-";

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Top Banner: Identity */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 shadow-lg relative overflow-hidden">
                {/* Decorative background accent */}
                <div className="absolute top-0 right-0 p-4 opacity-5">
                    {isPrinter ? (
                        <Printer className="w-48 h-48 text-white" />
                    ) : (
                        <Server className="w-48 h-48 text-white" />
                    )}
                </div>

                <div className="relative z-10 flex items-start justify-between">
                    <div className="space-y-2">
                        <div className="flex items-center">
                            <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                                {isPrinter ? (
                                    <Printer className="w-4 h-4 text-white" />
                                ) : (
                                    <Server className="w-4 h-4 text-white" />
                                )}
                            </div>
                            <div>
                                <h2 className="text-sm font-bold text-white leading-tight">
                                    {sysName || "Unknown Device"}
                                </h2>
                                <p className="text-[10px] text-gray-200 font-mono mt-0.5">{sysObjectId}</p>
                            </div>
                        </div>

                        <div className="pt-2 max-w-3xl">
                            <p className="text-gray-300 text-sm leading-relaxed border-l-2 border-gray-600 pl-3 italic">
                                "{description || "No description available"}"
                            </p>
                        </div>
                    </div>

                    {/* Status Badges */}
                    <div className="flex flex-col gap-2 items-end">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${isPrinter
                                ? "bg-blue-900/30 text-blue-400 border-blue-500/30"
                                : "bg-gray-700 text-gray-300 border-gray-600"
                            }`}>
                            {isPrinter ? "Printer Device" : "Network Device"}
                        </span>

                        {default_snmp_string && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-900/20 text-red-400 border border-red-500/30 flex items-center gap-1">
                                <ShieldAlert className="w-3 h-3" /> Default Credentials
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Location & Contact */}
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-5 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
                        Deployment Details
                    </h3>

                    <InfoRow
                        icon={<MapPin className="w-4 h-4 text-blue-400" />}
                        label="Device Location"
                        value={deviceLocation}
                        fallback="Location not configured"
                    />
                    <InfoRow
                        icon={<User className="w-4 h-4 text-green-400" />}
                        label="System Contact"
                        value={sysContact}
                        fallback="Contact not configured"
                    />
                    <InfoRow
                        icon={<Clock className="w-4 h-4 text-purple-400" />}
                        label="Last Updated"
                        value={lastUpdated}
                    />
                </div>

                {/* Hardware & Identifiers */}
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-5 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
                        Hardware & Config
                    </h3>

                    <InfoRow
                        icon={<Fingerprint className="w-4 h-4 text-yellow-400" />}
                        label="Serial Number"
                        value={serial}
                        isMono
                    />
                    <InfoRow
                        icon={<Hash className="w-4 h-4 text-indigo-400" />}
                        label="Object ID (OID)"
                        value={sysObjectId}
                        isMono
                    />

                    {/* Community String Display */}
                    <div className="flex items-start space-x-3 p-3 rounded-md bg-gray-900/50 border border-gray-700/50">
                        <div className="mt-0.5">
                            <Tag className="w-4 h-4 text-pink-400" />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-0.5">Community String</p>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-200 font-mono">
                                    {default_community_string}
                                </span>
                                {default_snmp_string && (
                                    <span className="text-[10px] text-red-400 bg-red-900/10 px-2 py-0.5 rounded border border-red-900/20">
                                        Default
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Raw OIDs Section (Only if OIDs exist) */}
            {data.oids && Object.keys(data.oids).length > 0 && (
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-5">
                    <div className="flex items-center gap-2 mb-4">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                            Additional OID Data
                        </h3>
                    </div>
                    <div className="bg-gray-900 rounded-md p-4 border border-gray-700 overflow-x-auto">
                        <pre className="text-xs text-gray-300 font-mono">
                            {JSON.stringify(data.oids, null, 2)}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper Sub-component for consistent rows
const InfoRow = ({
    icon,
    label,
    value,
    fallback = "-",
    isMono = false
}: {
    icon: React.ReactNode;
    label: string;
    value?: string;
    fallback?: string;
    isMono?: boolean;
}) => (
    <div className="flex items-start space-x-3 p-2 rounded-md hover:bg-gray-700/30 transition-colors">
        <div className="mt-0.5 opacity-80">{icon}</div>
        <div>
            <p className="text-xs text-gray-500 mb-0.5">{label}</p>
            <p className={`text-sm text-gray-200 ${isMono ? 'font-mono tracking-tight' : ''}`}>
                {(!value || value.trim() === "") ? <span className="text-gray-600 italic">{fallback}</span> : value}
            </p>
        </div>
    </div>
);

export default SnmpInfo;
